import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { isAdmin, getCurrentUser } from '../utils/auth';

export default function NavBar() {

    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const desktopDropdownRef = useRef(null);
    const mobileDropdownRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const hamburgerButtonRef = useRef(null);

    useEffect(() => {
        const currentUser = getCurrentUser();
        setUser(currentUser);
    }, [location.pathname]); // Re-fetch when route changes in case user info was updated

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't close if clicking on logout button
            if (event.target.closest('[data-logout-button="true"]')) {
                return;
            }
            
            // Check if click is outside desktop dropdown
            if (isDropdownOpen && desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            // Check if click is outside mobile dropdown
            if (isDropdownOpen && mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            // Check if click is outside mobile menu (but not on hamburger button)
            if (isMobileMenuOpen) {
                const isClickInsideMenu = mobileMenuRef.current && mobileMenuRef.current.contains(event.target);
                const isClickOnHamburger = hamburgerButtonRef.current && hamburgerButtonRef.current.contains(event.target);
                
                if (!isClickInsideMenu && !isClickOnHamburger) {
                    setIsMobileMenuOpen(false);
                }
            }
        };

        if (isDropdownOpen || isMobileMenuOpen) {
            // Use click instead of mousedown to ensure logout button click fires first
            document.addEventListener('click', handleClickOutside, true);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [isDropdownOpen, isMobileMenuOpen]);

    const handleLogout = (e) => {
        // Prevent event propagation to avoid conflicts with click-outside handler
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Clear the token and user info from local storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Close dropdown
        setIsDropdownOpen(false);

        // Force a full page reload to clear all state and redirect to login
        window.location.href = '/login';
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

  return (
    <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex h-16 justify-between items-center">
            <div className="flex items-center">
              <div className="flex flex-shrink-0 items-center">
                <img
                  alt="OI Banner App"
                  src="https://img-2.outlookindia.com/outlookindia/outlook-img/ol-logo-sub.jpg"
                  className="h-8 w-auto"
                />
              </div>
              {/* Desktop Navigation */}
              <nav className="hidden md:ml-8 md:flex md:space-x-1">
                <Link
                  to="/banners"
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    location.pathname === '/banners' || location.pathname.startsWith('/add-banner')
                      ? 'bg-red-50 text-red-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Banners
                </Link>
                <Link
                  to="/stats"
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    location.pathname === '/stats'
                      ? 'bg-red-50 text-red-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Stats
                </Link>
                {isAdmin() && (
                  <Link
                    to="/banner-types"
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === '/banner-types'
                        ? 'bg-red-50 text-red-700'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Banner Types
                  </Link>
                )}
                {isAdmin() && (
                  <Link
                    to="/users"
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === '/users'
                        ? 'bg-red-50 text-red-700'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Users
                  </Link>
                )}
              </nav>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Desktop User Dropdown */}
              <div className="hidden md:block relative" ref={desktopDropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {user && (
                    <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
                      {user.name || user.email}
                    </span>
                  )}
                  {user ? (
                    <div className="h-9 w-9 rounded-full bg-red-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm hover:bg-red-700 transition-all duration-200 transform hover:scale-110">
                      {user.name 
                        ? user.name.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2)
                        : user.email[0].toUpperCase()
                      }
                    </div>
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-slate-400 flex items-center justify-center text-white text-sm font-semibold">
                      ?
                    </div>
                  )}
                  <svg
                    className={`h-4 w-4 text-slate-500 transition-transform duration-300 ease-in-out ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div 
                    className="absolute right-0 mt-2 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transform transition-all duration-300 ease-out origin-top-right opacity-0 translate-y-[-8px] animate-[fadeInSlide_0.3s_ease-out_forwards]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="py-1">
                      {user && (
                        <div className="px-4 py-3 border-b border-slate-100">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {user.name || 'User'}
                          </p>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {user.email}
                          </p>
                        </div>
                      )}
                      <button
                        onClick={handleLogout}
                        data-logout-button="true"
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 flex items-center gap-2 rounded-lg group"
                      >
                        <svg className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile User Avatar */}
              <div className="md:hidden relative" ref={mobileDropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="flex items-center rounded-lg p-2 hover:bg-slate-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {user ? (
                    <div className="h-9 w-9 rounded-full bg-red-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm hover:bg-red-700 transition-all duration-200 transform hover:scale-110">
                      {user.name 
                        ? user.name.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2)
                        : user.email[0].toUpperCase()
                      }
                    </div>
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-slate-400 flex items-center justify-center text-white text-sm font-semibold">
                      ?
                    </div>
                  )}
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transform transition-all duration-300 ease-out origin-top-right opacity-0 translate-y-[-8px] animate-[fadeInSlide_0.3s_ease-out_forwards]">
                    <div className="py-1">
                      {user && (
                        <div className="px-4 py-3 border-b border-slate-100">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {user.name || 'User'}
                          </p>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {user.email}
                          </p>
                        </div>
                      )}
                      <button
                        onClick={handleLogout}
                        data-logout-button="true"
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 flex items-center gap-2 rounded-lg group"
                      >
                        <svg className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Hamburger Menu Button */}
              <button
                ref={hamburgerButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMobileMenu();
                }}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle menu"
              >
                <div className="relative w-6 h-6">
                  <svg
                    className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  <svg
                    className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`md:hidden border-t border-slate-200 overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
          ref={mobileMenuRef}
        >
          <div className="px-4 pt-2 pb-4 space-y-1">
            <Link
              to="/banners"
              onClick={closeMobileMenu}
              className={`block px-3 py-2 text-base font-medium rounded-lg transition-all duration-200 transform hover:translate-x-1 ${
                location.pathname === '/banners' || location.pathname.startsWith('/add-banner')
                  ? 'bg-red-50 text-red-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Banners
            </Link>
            {isAdmin() && (
              <Link
                to="/banner-types"
                onClick={closeMobileMenu}
                className={`block px-3 py-2 text-base font-medium rounded-lg transition-all duration-200 transform hover:translate-x-1 ${
                  location.pathname === '/banner-types'
                    ? 'bg-red-50 text-red-700'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Banner Types
              </Link>
            )}
            {isAdmin() && (
              <Link
                to="/users"
                onClick={closeMobileMenu}
                className={`block px-3 py-2 text-base font-medium rounded-lg transition-all duration-200 transform hover:translate-x-1 ${
                  location.pathname === '/users'
                    ? 'bg-red-50 text-red-700'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Users
              </Link>
            )}
          </div>
        </div>
      </div>
  )
}
